// Copyright 2025, University of Maryland, All Rights Reserved
"use client"

import { useEffect } from "react"
import {
  type InitialConfigType,
  LexicalComposer,
} from "@lexical/react/LexicalComposer"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin"
import { type EditorState, type LexicalEditor, type SerializedEditorState } from "lexical"

import { editorTheme } from "@/components/editor/themes/editor-theme"
import { TooltipProvider } from "@/components/ui/tooltip"

import { nodes } from "./nodes"
import { Plugins } from "./plugins"

const editorConfig: InitialConfigType = {
  namespace: "Editor",
  theme: editorTheme,
  nodes,
  onError: (error: Error) => {
    console.error(error)
  },
}

// Plugin to capture editor reference immediately on mount
function EditorRefPlugin({ onEditorReady }: { onEditorReady?: (editor: LexicalEditor) => void }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    onEditorReady?.(editor)
  }, [editor, onEditorReady])

  return null
}

export function Editor({
  editorState,
  editorSerializedState,
  onChange,
  onSerializedChange,
  onEditorReady,
  ariaLabel,
  toolbar,
}: {
  editorState?: InitialConfigType["editorState"]
  editorSerializedState?: SerializedEditorState
  onChange?: (editorState: EditorState, editor: LexicalEditor) => void
  onSerializedChange?: (editorSerializedState: SerializedEditorState) => void
  onEditorReady?: (editor: LexicalEditor) => void
  ariaLabel?: string
  toolbar?: "full" | "minimal"
}) {
  return (
    <div className="bg-background overflow-hidden rounded-lg border shadow">
      <LexicalComposer
        initialConfig={{
          ...editorConfig,
          ...(editorState ? { editorState } : {}),
          ...(editorSerializedState
            ? { editorState: JSON.stringify(editorSerializedState) }
            : {}),
        }}
      >
        <TooltipProvider>
          <Plugins ariaLabel={ariaLabel} toolbar={toolbar} />

          <EditorRefPlugin onEditorReady={onEditorReady} />

          <OnChangePlugin
            ignoreSelectionChange={true}
            onChange={(editorState, editor) => {
              onChange?.(editorState, editor)
              onSerializedChange?.(editorState.toJSON())
            }}
          />
        </TooltipProvider>
      </LexicalComposer>
    </div>
  )
}
